import React, { useCallback, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  TextInput,
  maxLength,
  minLength,
  required,
  useLogin,
  useNotify,
  useSafeSetState,
} from 'react-admin';

import AuthLayout from '../../AuthLayout';
import InputGroup from '../../InputGroup';
import ButtonGroup from '../../ButtonGroup';

import authRoutes from '../../../../constants/authRoutes';
import errorNotification from '../../../../utilities/errorNotification';
import { mfaChallengeStore } from '../../../../utilities/authTokenStore';

const TOTP_CODE_LENGTH = 6;
const RECOVERY_CODE_LENGTH = 16;

type MfaChallengeFormFields = {
  totpCode?: string;
  recoveryCode?: string;
}

const MfaChallengePage = () => {
  const [loading, setLoading] = useSafeSetState(false);
  const [usingRecoveryCode, setUsingRecoveryCode] = useSafeSetState(false);
  const login = useLogin();
  const notify = useNotify();
  const navigate = useNavigate();

  const challengeToken = mfaChallengeStore.getToken();

  const backToSignIn = useCallback(() => {
    mfaChallengeStore.removeToken();
    navigate(authRoutes.LOGIN, { replace: true });
  }, [navigate]);

  const useRecoveryCode = useCallback(() => {
    setUsingRecoveryCode(true);
  }, [setUsingRecoveryCode]);

  const useAuthenticatorApp = useCallback(() => {
    setUsingRecoveryCode(false);
  }, [setUsingRecoveryCode]);

  useEffect(() => {
    if (!challengeToken) navigate(authRoutes.LOGIN, { replace: true });
  }, [challengeToken, navigate]);

  const submit = useCallback(async ({ totpCode, recoveryCode }: MfaChallengeFormFields) => {
    const code = usingRecoveryCode ? recoveryCode : totpCode;
    if (!challengeToken || !code) return;

    setLoading(true);
    try {
      await login({
        challengeToken,
        ...(usingRecoveryCode ? { recoveryCode: code } : { totpCode: code }),
      });
    } catch (error) {
      notify(errorNotification(error, 'Verification failed'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [challengeToken, login, notify, setLoading, usingRecoveryCode]);

  if (!challengeToken) return null;

  return (
    <AuthLayout>
      <Typography component="h1" variant="h4" align="center" gutterBottom>
        Two-step verification
      </Typography>
      <Typography component="h2" variant="body1" align="center">
        {usingRecoveryCode
          ? 'Enter one of the recovery codes you saved when you set up two-step verification'
          : 'Enter the code from your authenticator app to finish signing in'}
      </Typography>
      <Form onSubmit={submit} key={usingRecoveryCode ? 'recoveryCode' : 'totpCode'}>
        <InputGroup>
          {usingRecoveryCode ? (
            <TextInput
              source="recoveryCode"
              label="Recovery code"
              disabled={loading}
              validate={[required(), minLength(RECOVERY_CODE_LENGTH)]}
              fullWidth
              autoFocus
            />
          ) : (
            <TextInput
              source="totpCode"
              label="Verification code"
              disabled={loading}
              validate={[required(), minLength(TOTP_CODE_LENGTH), maxLength(TOTP_CODE_LENGTH)]}
              fullWidth
              autoFocus
            />
          )}
        </InputGroup>
        <ButtonGroup sx={{ mt: 6 }}>
          <Button
            variant="contained"
            type="submit"
            disabled={loading}
          >
            {loading && (
              <CircularProgress
                size={18}
                thickness={2}
              />
            )}
            Verify
          </Button>
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              component="button"
              type="button"
              onClick={usingRecoveryCode ? useAuthenticatorApp : useRecoveryCode}
              disabled={loading}
            >
              {usingRecoveryCode
                ? 'Use your authenticator app instead'
                : 'Use a recovery code instead'}
            </Link>
          </Typography>
          <Button
            onClick={backToSignIn}
            disabled={loading}
          >
            Back to sign in
          </Button>
        </ButtonGroup>
      </Form>
    </AuthLayout>
  );
};

export default MfaChallengePage;
